/**
 * TanStack Query functions for booking-related API calls
 */

import { buildApiUrl } from "../config/urlConfig.js";
import { queryKeys } from "../queryKeys.js";

/**
 * Expand date range to full months for better caching
 * @param {string} start - Start date
 * @param {string} end - End date
 * @returns {Object} Expanded date range
 */
const expandToMonth = (start, end) => {
	const startDate = new Date(`${start}T00:00:00Z`);
	const endDate = new Date(`${end}T00:00:00Z`);

	const expandedStart = new Date(
		Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1)
	);
	const expandedEnd = new Date(
		Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() + 1, 0)
	);

	return {
		start: expandedStart.toISOString().slice(0, 10),
		end: expandedEnd.toISOString().slice(0, 10),
	};
};

/**
 * Fetch bookings for a specific room
 * @param {number} roomId - Room ID
 * @param {Object} dateRange - Date range with start and end properties
 * @returns {Promise<Object>} Booking data with bookings, opening hours, and permissions
 */
export const fetchBookingsForRoom = async (roomId, dateRange) => {
	const expandedRange = expandToMonth(dateRange.start, dateRange.end);

	const params = {
		roomId: roomId,
		from: expandedRange.start,
		until: expandedRange.end,
	};

	const url = buildApiUrl("user", "/fetch-bookings-for-room");
	const response = await fetch(url, {
		method: "POST",
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new Error(`HTTP ${response.status}: Failed to fetch bookings`);
	}

	const data = await response.json();

	if (data.error) {
		throw new Error(data.error);
	}

	return {
		bookings: data.bookings || [],
		openingHours: data.openingHoursByDay,
		holidays: data.holidays || {},
		fetchedRange: expandedRange,
	};
};

/**
 * Fetch all bookings across all rooms
 * @param {string} [date] - Optional date filter
 * @returns {Promise<Object>} All bookings data
 */
export const fetchAllBookings = async (date) => {
	const params = date ? { date } : {};

	const url = buildApiUrl("user", "/fetch-all-bookings");
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		credentials: "include",
		body: JSON.stringify(params),
	});

	const data = await response.json();

	if (!response.ok && response.status === 403 && !data.authenticated) {
		console.log("Session Timeout detected.");
		window.location.reload();
	}
	if (!response.ok) {
		throw new Error(`HTTP ${response.status}: Failed to fetch all bookings`);
	}

	if (data.error) {
		throw new Error(data.error);
	}

	return data;
};

/**
 * TanStack Query configuration for fetching bookings by room
 * @param {number} roomId - Room ID
 * @param {Object} dateRange - Date range
 * @returns {Object} Query configuration object
 */
export const useBookingsByRoomQuery = (roomId, dateRange) => {
	return {
		queryKey: queryKeys.bookingsByRoom(roomId, dateRange),
		queryFn: ({ signal }) => {
			// Abort controller support for cancellation
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}
			return fetchBookingsForRoom(roomId, dateRange);
		},
		staleTime: 1000 * 10, // 10 seconds (matching multi-room for consistency)
		refetchInterval: 1000 * 15, // 15 seconds polling for real-time updates
		gcTime: 1000 * 60 * 30, // 30 minutes (previously cacheTime)
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
		enabled: !!roomId && !!dateRange?.start && !!dateRange?.end,
	};
};

/**
 * Fetch logs for a specific booking
 * @param {number} bookingId - Booking ID
 * @returns {Promise<Object>} Booking logs data
 */
export const fetchBookingLogs = async (bookingId) => {
	const params = { bookingId };

	const url = buildApiUrl("user", "/fetch-logs");
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		credentials: "include",
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new Error(`HTTP ${response.status}: Failed to fetch booking logs`);
	}

	const data = await response.json();

	if (data.error) {
		throw new Error(data.error);
	}

	return data;
};

/**
 * TanStack Query configuration for fetching all bookings
 * @param {string} [date] - Optional date filter
 * @returns {Object} Query configuration object
 */
export const useAllBookingsQuery = (date) => {
	return {
		queryKey: queryKeys.allBookings(date),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}
			return fetchAllBookings(date);
		},
		staleTime: 1000 * 10, // 10 seconds (shorter for multi-room view)
		refetchInterval: 1000 * 15, // 15 seconds polling for real-time updates
		gcTime: 1000 * 60 * 15, // 15 minutes
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	};
};

/**
 * TanStack Query configuration for fetching booking logs
 * @param {number} bookingId - Booking ID
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {Object} Query configuration object
 */
export const getConcertRequestPrefillQueryOptions = (concertRequestId) => ({
	queryKey: queryKeys.concertRequestPrefill(concertRequestId),
	queryFn: async ({ signal }) => {
		const encodedId = encodeURIComponent(concertRequestId);
		const url = `/event-management/api/concert-request/${encodedId}/room-booking-prefill`;
		const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
		const json = await response.json();
		if (!response.ok || json?.error) {
			throw new Error(json?.error || response.statusText);
		}
		if (!json?.prefillData) {
			throw new Error("No prefill data in response");
		}
		return json.prefillData;
	},
	staleTime: Infinity,
	retry: false,
});

export const useBookingLogsQuery = (bookingId, enabled = true) => {
	return {
		queryKey: queryKeys.bookingLogs(bookingId),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}
			return fetchBookingLogs(bookingId);
		},
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 30, // 30 minutes
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
		enabled: !!bookingId && enabled,
	};
};
